import AES from 'crypto-js/aes';
import Utf8 from 'crypto-js/enc-utf8';

// ⚠️ SECURITY HONESTY NOTE — read before relying on this.
//
// This AES wrapper is OBFUSCATION against casual inspection only (someone
// glancing at localStorage in DevTools). It provides NO protection against
// XSS or local malware: the key below ships in the public JS bundle, and any
// script running in the page can simply call getToken(). It cannot be made
// real from the frontend alone — the mDash API requires the token in
// JavaScript-accessible form (?access_token= URLs), so any frontend-derived
// key is reachable by the same attackers. Do NOT treat tokens as
// encrypted-at-rest; the actual XSS defense is the CSP in _headers.
//
// TODO(SupaDash): when auth moves off mDash, store the session in an
// httpOnly cookie (e.g. Supabase auth with @supabase/ssr cookie sessions)
// so the credential never touches JavaScript at all, and delete this file's
// encrypt/decrypt layer (keep a one-release migration that clears ktok).
// Audit ref: AUDIT-2026-06-11.md finding H4.
const ENCRYPTION_KEY = 'kegtron-webapp-v1-key';

// The localStorage key the session token lives under. Shared by every tab of
// the browser profile — see services/sessionSync.js.
export const TOKEN_STORAGE_KEY = 'ktok';

// The last token THIS tab stored with setToken (null after clearToken, or
// once the tab adopted another tab's token). Lets sessionSync tell "this tab
// just signed in and App's setState({ ktok }) has not committed yet" apart
// from "another tab replaced the token" — the stored token differs from
// app.state.ktok in both cases.
let ownTokenWrite = null;

/**
 * Encrypts a token using AES encryption before storing in localStorage
 * @param {string} token - The authentication token to encrypt
 * @returns {string} Encrypted token
 */
export function encryptToken(token) {
  if (!token) return null;
  try {
    return AES.encrypt(token, ENCRYPTION_KEY).toString();
  } catch {
    return token; // Fallback to unencrypted if encryption fails
  }
}

/**
 * Decrypts a token retrieved from localStorage
 * @param {string} encryptedToken - The encrypted token
 * @returns {string} Decrypted token
 */
export function decryptToken(encryptedToken) {
  if (!encryptedToken) return null;
  try {
    const bytes = AES.decrypt(encryptedToken, ENCRYPTION_KEY);
    const decrypted = bytes.toString(Utf8);
    // If decryption returns empty string, token might be unencrypted (legacy)
    return decrypted || encryptedToken;
  } catch {
    // Return original if decryption fails (backward compatibility)
    return encryptedToken;
  }
}

/**
 * Safely stores an encrypted token in localStorage
 * @param {string} token - The token to store
 */
export function setToken(token) {
  if (!token) {
    ownTokenWrite = null;
    delete localStorage.ktok;
    return;
  }
  ownTokenWrite = token;
  const encrypted = encryptToken(token);
  localStorage.ktok = encrypted;
}

/**
 * True when `token` is the one this tab last stored itself. A removal is
 * never "own": another tab's sign-out must always be noticed.
 * @param {string|null} token
 * @returns {boolean}
 */
export function isOwnTokenWrite(token) {
  return !!token && token === ownTokenWrite;
}

/**
 * Forget this tab's last own write. Called when the tab adopts a token
 * another tab stored, so a later switch back to this tab's old token is
 * still recognised as coming from elsewhere.
 */
export function forgetOwnTokenWrite() {
  ownTokenWrite = null;
}

/**
 * Retrieves and decrypts a token from localStorage
 * @returns {string|null} The decrypted token or null if not found
 */
export function getToken() {
  const encrypted = localStorage.ktok;
  if (!encrypted) return null;
  return decryptToken(encrypted);
}

/**
 * Removes the token from localStorage and purges the API response cache
 * so that customer/device data does not linger in Cache Storage on shared
 * machines. The cache delete is fire-and-forget; logout never blocks on it.
 */
export function clearToken() {
  ownTokenWrite = null;
  delete localStorage.ktok;
  if (typeof caches !== 'undefined' && caches.delete) {
    caches.delete('api-cache').catch(() => {});
  }
}

/**
 * Clears sensitive data from the current URL without page reload
 * This prevents credentials from appearing in browser history
 */
export function clearUrlCredentials() {
  if (typeof window !== 'undefined' && window.location.href.indexOf('?') > 0) {
    const cleanUrl = window.location.href.split('?')[0];
    // Use replaceState to avoid page reload and prevent history entry
    window.history.replaceState({}, document.title, cleanUrl);
  }
}
